import { isApiErrorBody } from '@leadgab/shared';
import type { ApiErrorCode, MessageType, SessionType } from '@leadgab/shared';

/**
 * Typed fetch wrapper.
 *
 * Everything goes through here so failures surface as one exception type that
 * carries the per-field messages the forms render inline.
 */

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode | 'network';
  readonly fieldErrors: Record<string, string>;

  constructor(
    message: string,
    status: number,
    code: ApiErrorCode | 'network',
    fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: Query): string {
  const url = `/api${path}`;
  if (!query) return url;

  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }

  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      // The session lives in an httpOnly cookie, so every call must send it.
      credentials: 'include',
      headers: options.body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch {
    throw new ApiRequestError('Cannot reach the server', 0, 'network');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    if (isApiErrorBody(payload)) {
      throw new ApiRequestError(
        payload.error.message,
        response.status,
        payload.error.code,
        payload.error.fieldErrors ?? {},
      );
    }

    throw new ApiRequestError('Something went wrong', response.status, 'internal_error');
  }

  return payload as T;
}

/**
 * Uploads images and returns the descriptors to attach to a message.
 *
 * Multipart, so it bypasses `request()` — the browser has to set its own
 * `Content-Type` with the boundary.
 */
/**
 * Replaces the signed-in user's avatar.
 *
 * The server resizes and re-encodes it, so anything a browser will open is
 * acceptable here and what lands on disk is always a square JPEG.
 */
export async function uploadAvatar(file: File): Promise<{ url: string }> {
  const form = new FormData();
  form.append('file', file);

  let response: Response;

  try {
    // No Content-Type header: the browser has to set it, because only it knows
    // the multipart boundary it just generated.
    response = await fetch('/api/uploads/avatar', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
  } catch {
    throw new ApiRequestError('Cannot reach the server', 0, 'network');
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiRequestError(
      isApiErrorBody(payload) ? payload.error.message : 'Upload failed',
      response.status,
      isApiErrorBody(payload) ? payload.error.code : 'bad_request',
    );
  }

  return payload as { url: string };
}

export async function uploadAttachments(files: File[]): Promise<AttachmentDto[]> {
  const form = new FormData();
  for (const file of files) form.append('files', file);

  let response: Response;

  try {
    response = await fetch('/api/uploads/attachments', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
  } catch {
    throw new ApiRequestError('Cannot reach the server', 0, 'network');
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (isApiErrorBody(payload)) {
      throw new ApiRequestError(
        payload.error.message,
        response.status,
        payload.error.code,
        payload.error.fieldErrors ?? {},
      );
    }

    throw new ApiRequestError('Upload failed', response.status, 'internal_error');
  }

  return (payload as { files: AttachmentDto[] }).files;
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// --- response shapes --------------------------------------------------------

export interface WireUserDto {
  id: number | null;
  displayName: string;
  avatar: string | null;
}

export interface SessionDto {
  id: number;
  uuid: string;
  type: SessionType;
  assignedToId: number | null;
  customerName: string;
  customerAvatar: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  archived: boolean;
  spam: boolean;
}

export interface SessionListItemDto extends SessionDto {
  category: 'new' | 'my';
  unread: number;
  updatedAt: string;
  assignedTo: WireUserDto | null;
  lastMessage: {
    content: string;
    createdAt: string;
    type: MessageType;
    fromCustomer: boolean;
  } | null;
}

export interface SessionDetailDto extends SessionDto {
  assignedTo: WireUserDto | null;
  createdAt: string;
  updatedAt: string;
  unsubscribed: { email: boolean; sms: boolean };
}

export interface AttachmentDto {
  url: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  name?: string;
}

export interface MessageDto {
  id: number;
  sessionId: number;
  sessionUuid: string;
  type: MessageType;
  content: string;
  attachments: AttachmentDto[];
  createdAt: string;
  user: WireUserDto;
  meta: Record<string, unknown> & {
    system?: boolean;
    notice?: boolean;
    source?: string;
    status?: 'sent' | 'failed';
    /** Which route carried it, set alongside `status` on outgoing messages. */
    channel?: 'sms' | 'email' | 'fmp';
    actions?: { type: 'call'; value: string; label: string }[];
  };
}

export interface MeDto {
  user: WireUserDto;
  role: 'owner' | 'admin' | 'agent';
  email: string;
  /** `smsConfigured` says whether the workspace has a Twilio number connected. */
  chat: { uuid: string; title: string; smsConfigured: boolean };
  preferences: { sendWithEnter: boolean; soundEnabled: boolean; soundVolume: number; theme: string };
}

export interface Paged<T> {
  items: T[];
  nextCursor: number | null;
}
